import { Component, OnInit, Optional, Inject, ViewChild } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, filter, finalize, map, tap, takeUntil } from "rxjs/operators";
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatAutocomplete, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { RoleMaintenanceService } from '../../role-maintenance/role-maintenance.service';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { of as observableOf, Subscription, fromEvent } from 'rxjs';
import { LanguageService, DialogService } from 'src/app/services';
import { environment } from 'src/environments/environment';
import { MediaObserver } from '@angular/flex-layout';
import * as _ from 'lodash';
import { requestRole } from 'src/app/model/requests';

@Component({
  selector: 'esc-web-user-filter',
  templateUrl: './web-user-filter.component.html',
  styleUrls: ['./web-user-filter.component.scss']
})
export class WebUserFilterComponent implements OnInit {
  @ViewChild('roleAC') roleACRef: MatAutocomplete;
  @ViewChild(MatAutocompleteTrigger) roleACTr: MatAutocompleteTrigger;

  constructor(
    private fb: FormBuilder,
    public media: MediaObserver,
    public roleService: RoleMaintenanceService,
    private dialogService: DialogService,
    private languageService: LanguageService,
    public dialogRef: MatDialogRef<WebUserFilterComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) { }

  public loading = {
    role: false,
    status: false,
    error: false
  };
  private getDataSub: Subscription;
  public defaultOption = {
    code: "all",
    description: "All"
  };
  roles = [];
  statuses = [
    {
      code: null,
      description: "All",
    },
    {
      code: 1,
      description: "Active",
    },
    {
      code: 0,
      description: "Inactive",
    },
  ];
  public filters;

  public formGroup: FormGroup;
  networkErrMsg = this.languageService.getTranslation("MESSAGE_ERROR_NETWORK_CONNECTION", {});

  ngOnInit() {
    this.filters = _.cloneDeep(INITIAL_OPTIONS);
    this.initForm();
    this.detectFormChanges();
  }

  initForm() {
    this.formGroup = this.fb.group({
      role: [_.get(this.data, 'role') || this.defaultOption, Validators.required],
      status: [_.get(this.data, 'status') || this.statuses[0], Validators.required]
    })
  }

  compareWithFunc = (a: any, b: any) => (a && b) ? a.code == b.code : null;

  detectFormChanges() {
    this.formGroup.get("role").valueChanges.pipe(
      debounceTime(600),
      filter((term) => !_.isObject(term)),
      distinctUntilChanged(),
      map((term: string) => _.trim(term)),
    )
    .subscribe(value => {
      this.filters.web_filter.keyword = _.toString(value);
      this.roles = [];
      this.getData("role");
    });
  }

  getInitialData(name) {
    this.autoCmpltClosed(name);
    this.filters = _.cloneDeep(INITIAL_OPTIONS);
    this.getData(name);
  }
 
  getMoreData(name) {
    if (this.filters.getMore) {
      this.filters.web_filter.offset += INITIAL_OPTIONS.web_filter.limit;
      this.getData(name);
    }
  }
 
  getData(name) {
    let filters;
    this.filters.getMore = false;
    this.loading[name] = true;
 
    switch (name) {
      case "role": {
        filters = _.assign(filters, requestRole);
        filters.web_filter.offset = _.cloneDeep(this.filters.web_filter.offset);

        this.roleService.getList(filters).pipe(
          tap(() => this.loading.role = true),
          finalize(() => {
            this.loading.role = false
            return observableOf(null);
          }),
          catchError((err) => {
            console.error(err);
            this.loading.role = false;
            return observableOf(null);
          }))
          .subscribe((res: any) => {
            this.loading.role = false;
            if (environment.showLogs) {
              console.log(res);
            }
            if (res == null || res.status == 0 || res.status == 504 || res.status == 500) {
              this.loading.error = true;
              this.roles = [];
              this.dialogService.openSnackBar(this.networkErrMsg)
              return;
            }
            if (_.has(res, 'value') && (res.value == "Success")) {
              if(!filters.web_filter.keyword && !this.formGroup.get('role').value) {
                this.roles[0] = this.defaultOption;
              }

              this.roles = this.roles.concat(res.data);
             
              if((res.data.length > 0) && (this.roles.length < _.get(res, 'count'))) {
                this.filters.getMore = true;
              }
            }
          });
          break;
      }
    }
  }

  autoCmpltClosed(name) {
    let options = name + "s";
    this[options] = [];
  }

  resetControl(control: FormControl) {
    control.reset(null, { emitEvent: false });
  }
 
  displayFn(data?: any): string | undefined {
    if(data) {
      return _.get(data, 'code') != "all" ? (_.get(data, 'code') + ' - ' + _.get(data, 'description')) : _.get(data, 'description');
    }
    return null;
  }
 
  autocompleteScroll(name, refName, acTrigger) {
    setTimeout(() => {
      if (this[refName].isOpen || this[refName].panel) {
        fromEvent(this[refName].panel.nativeElement, 'scroll')
          .pipe(
            map(x => this[refName].panel.nativeElement.scrollTop),
            takeUntil(this[acTrigger].panelClosingActions)
          )
          .subscribe(x => {
            const scrollTop = this[refName].panel.nativeElement
              .scrollTop;
            const scrollHeight = this[refName].panel.nativeElement
              .scrollHeight;
            const elementHeight = this[refName].panel.nativeElement
              .clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              // fetch more data
              this.getMoreData(name);
            }
          });
      }
    });
  }

  applyFilters() {
    for (let ctrl in this.formGroup.controls) {
      if(!this.formGroup.controls[ctrl].valid) {
        this.formGroup.controls[ctrl].markAsTouched();
      }
    }

    if(this.formGroup.valid) {
      this.dialogRef.close(this.formGroup.value);
    }
  }

  reset() {
    for(let ctrl in this.formGroup.controls) {
      this.formGroup.controls[ctrl].reset(null, {emitEvent: false});
      this.formGroup.controls[ctrl].setValue(this.defaultOption, {emitEvent: false});
      this.formGroup.controls[ctrl].markAsUntouched();
    }
    this.formGroup.get('status').setValue(this.statuses[0]);
  }
}

const INITIAL_OPTIONS = {
	web_filter: {
		limit: 50,
		offset: 0,
    keyword: "",
    order_by: "asc",
    role_id: 0,
		is_asc: 1
	}
}